import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
    Input, Sheet, Stack, Typography, Alert
} from "@mui/joy";
import { useEffect, useState } from "react";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';

import {
    APIReferralGetMyCode,
    APIReferralGetStats,
    APIReferralGetBalance,
    APIReferralGetSettings,
    APIReferralApplyCode,
} from "../../api";


export default function ReferralPage() {
    const [code, setCode] = useState(null);
    const [stats, setStats] = useState(null);
    const [balance, setBalance] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Поле ввода чужого кода
    const [inputCode, setInputCode] = useState('');
    const [applying, setApplying] = useState(false);
    const [applyMsg, setApplyMsg] = useState(null);
    
    // Toast копирования
    const [copyMsg, setCopyMsg] = useState(null);
    
    const loadAll = async () => {
        setLoading(true);
        const [c, s, b, st] = await Promise.all([
            APIReferralGetMyCode(),
            APIReferralGetStats(),
            APIReferralGetBalance(),
            APIReferralGetSettings(),
        ]);
        setCode(c);
        setStats(s);
        setBalance(b);
        setSettings(st);
        setLoading(false);
    };
    
    useEffect(() => { loadAll(); }, []);
    
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopyMsg('Скопировано!');
        setTimeout(() => setCopyMsg(null), 1500);
    };
    
    const handleShare = async () => {
        if (!code?.link) return;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Реферальная программа',
                    text: settings?.terms_text || 'Скидка на первый замер по моей ссылке',
                    url: code.link,
                });
            } catch (_) {}
        } else {
            handleCopy(code.link);
        }
    };
    
    const handleApplyCode = async () => {
        if (!inputCode.trim()) return;
        setApplying(true);
        setApplyMsg(null);
        const res = await APIReferralApplyCode(inputCode.trim().toUpperCase());
        setApplying(false);
        if (res?.status === 'ok') {
            setApplyMsg({ type: 'success', text: 'Код применён! Скидка действует на следующую заявку.' });
            setInputCode('');
            loadAll();
        } else {
            setApplyMsg({ type: 'error', text: res?.message || 'Не удалось применить код' });
        }
    };
    
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }
    
    if (settings && !settings.is_active) {
        return (
            <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
                <Typography level="h3" sx={{ mb: 2 }}>Реферальная программа</Typography>
                <Alert color="warning">
                    Программа временно не активна. Загляните позже.
                </Alert>
            </Box>
        );
    }
    
    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            <Typography level="h3" sx={{ mb: 2, fontWeight: 800 }}>
                🎁 Реферальная программа
            </Typography>
            
            {/* ── Условия программы ── */}
            {settings && (
                <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'xl', mb: 2 }}>
                    <Typography level="body-sm" sx={{ fontWeight: 700, mb: 1 }}>
                        Как это работает:
                    </Typography>
                    <Typography level="body-sm">
                        Поделитесь ссылкой с другом — он получит скидку <strong>{settings.invitee.percent}% от суммы заявки</strong> (от {settings.invitee.min_amount} до {settings.invitee.max_amount} ₽) на первый замер.
                        <br />
                        Вам начислится <strong>{settings.referrer.percent}%</strong> бонусом (от {settings.referrer.min_amount} до {settings.referrer.max_amount} ₽) после {settings.reward_trigger === 'on_event_completed' ? 'завершения замера' : 'оформления заявки'}.
                    </Typography>
                    {settings.terms_text && (
                        <Typography level="body-xs" sx={{ mt: 1.5, color: 'text.secondary' }}>
                            {settings.terms_text}
                        </Typography>
                    )}
                </Sheet>
            )}
            
            {/* ── Мой код и ссылка ── */}
            {code && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <CardGiftcardRoundedIcon color="primary" />
                            <Typography level="title-md">Ваш код</Typography>
                        </Stack>
                        
                        <Sheet variant="outlined" sx={{
                            p: 1.5, borderRadius: 'lg', mb: 1.5,
                            textAlign: 'center', bgcolor: 'primary.50',
                        }}>
                            <Typography sx={{
                                fontFamily: 'monospace', fontSize: '1.5rem', fontWeight: 800,
                                letterSpacing: '0.15em', color: 'primary.700',
                            }}>
                                {code.code}
                            </Typography>
                        </Sheet>
                        
                        <Stack direction="row" spacing={1}>
                            <Button
                                fullWidth variant="solid" color="primary"
                                startDecorator={<ShareRoundedIcon />}
                                onClick={handleShare}
                            >
                                Поделиться
                            </Button>
                            <Button
                                fullWidth variant="soft" color="primary"
                                startDecorator={<ContentCopyRoundedIcon />}
                                onClick={() => handleCopy(code.link)}
                            >
                                Скопировать
                            </Button>
                        </Stack>
                        
                        {copyMsg && (
                            <Typography level="body-xs" sx={{
                                mt: 1, textAlign: 'center', color: 'success.600',
                            }}>
                                {copyMsg}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* ── Баланс ── */}
            {balance && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <AccountBalanceWalletRoundedIcon color="success" />
                            <Typography level="title-md">Баланс</Typography>
                        </Stack>
                        
                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <Box sx={{ flex: 1, p: 1.5, borderRadius: 'lg', bgcolor: 'success.50', textAlign: 'center' }}>
                                <Typography level="body-xs" color="neutral">Доступно</Typography>
                                <Typography level="h4" sx={{ fontWeight: 800, color: 'success.700' }}>
                                    {Number(balance.balance).toFixed(0)} ₽
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, p: 1.5, borderRadius: 'lg', bgcolor: 'neutral.50', textAlign: 'center' }}>
                                <Typography level="body-xs" color="neutral">Всего заработано</Typography>
                                <Typography level="title-md" sx={{ fontWeight: 700 }}>
                                    {Number(balance.total_earned).toFixed(0)} ₽
                                </Typography>
                            </Box>
                        </Stack>
                        
                        {balance.transactions && balance.transactions.length > 0 && (
                            <>
                                <Divider sx={{ mb: 1 }} />
                                <Typography level="body-xs" sx={{ fontWeight: 700, mb: 1, color: 'text.tertiary', textTransform: 'uppercase' }}>
                                    История
                                </Typography>
                                <Stack spacing={0.5} sx={{ maxHeight: 240, overflowY: 'auto' }}>
                                    {balance.transactions.slice(0, 15).map(t => (
                                        <Stack key={t.id} direction="row" justifyContent="space-between" sx={{ py: 0.5, px: 1, borderRadius: 'sm', bgcolor: 'background.level1' }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography level="body-sm" noWrap>{t.comment || t.type}</Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    {new Date(t.date).toLocaleDateString('ru-RU')}
                                                </Typography>
                                            </Box>
                                            <Typography level="body-sm" sx={{
                                                fontWeight: 700,
                                                color: t.amount > 0 ? 'success.600' : 'danger.600',
                                            }}>
                                                {t.amount > 0 ? '+' : ''}{Number(t.amount).toFixed(0)} ₽
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* ── Приглашения ── */}
            {stats && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <GroupRoundedIcon color="primary" />
                            <Typography level="title-md">Приглашённые</Typography>
                        </Stack>
                        
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Chip variant="soft" color="neutral">Всего: {stats.total_invited}</Chip>
                            <Chip variant="soft" color="primary">С заявкой: {stats.with_applications}</Chip>
                            <Chip variant="soft" color="success">Выплачено: {stats.rewarded}</Chip>
                        </Stack>
                        
                        {stats.invites && stats.invites.length > 0 ? (
                            <Stack spacing={0.5} sx={{ maxHeight: 240, overflowY: 'auto' }}>
                                {stats.invites.map(inv => (
                                    <Stack key={inv.id} direction="row" justifyContent="space-between" sx={{ py: 0.75, px: 1, borderRadius: 'md', bgcolor: 'background.level1' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography level="body-sm" noWrap>
                                                {inv.invitee_phone || '—'}
                                            </Typography>
                                            <Typography level="body-xs" color="neutral">
                                                {inv.status === 'rewarded'         && '✅ Бонус начислен'}
                                                {inv.status === 'first_application'&& '⏳ Ждём завершения замера'}
                                                {inv.status === 'registered'       && '👤 Зарегистрирован'}
                                                {inv.status === 'canceled'         && '❌ Отменён'}
                                                {' · '}
                                                {new Date(inv.date_create).toLocaleDateString('ru-RU')}
                                            </Typography>
                                        </Box>
                                        {inv.reward_referrer > 0 && (
                                            <Typography level="body-sm" sx={{ fontWeight: 700, color: 'success.600' }}>
                                                +{Number(inv.reward_referrer).toFixed(0)} ₽
                                            </Typography>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>
                        ) : (
                            <Typography level="body-sm" color="neutral" sx={{ fontStyle: 'italic' }}>
                                Пока никого не приглашали. Поделитесь ссылкой выше.
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* ── Ввести чужой код ── */}
            <Card>
                <CardContent>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                        Друг прислал вам код?
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Input
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                            placeholder="ABC12345"
                            sx={{ flex: 1, fontFamily: 'monospace', textTransform: 'uppercase' }}
                            slotProps={{ input: { maxLength: 12 } }}
                        />
                        <Button
                            variant="solid" color="primary"
                            loading={applying}
                            disabled={!inputCode.trim()}
                            onClick={handleApplyCode}
                        >
                            Применить
                        </Button>
                    </Stack>
                    {applyMsg && (
                        <Alert
                            color={applyMsg.type === 'success' ? 'success' : 'danger'}
                            variant="soft"
                            sx={{ mt: 1.5 }}
                        >
                            {applyMsg.text}
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}